import React, { useEffect, useMemo, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Sector } from 'recharts';
import { I18n } from 'react-redux-i18n';
import { splitCenterLabel } from './profileCategories.utils';
import './ProfileCategoriesDonutChart.sass';

const PROFILE_CATEGORY_COLORS = [
  '#508ff4',
  '#50f48f',
  '#f4f450',
  '#f48f50',
  '#f4508f',
];

const PROFILE_CATEGORY_EMPTY_COLOR = '#dcebf9';
const PROFILE_CATEGORY_DISABLED_COLOR = '#d0d7e2';
const PROFILE_CATEGORY_OTHER_LABEL_COLOR = '#8b95a5';

const getViewportWidth = () =>
  typeof window !== 'undefined' ? window.innerWidth : 0;

const getDonutLayout = (viewportWidth) => {
  if (viewportWidth < 360) {
    return {
      chartHeight: 225,
      innerRadius: 48,
      outerRadius: 78,
    };
  }

  if (viewportWidth < 480) {
    return {
      chartHeight: 240,
      innerRadius: 58,
      outerRadius: 92,
    };
  }

  return {
    chartHeight: 275,
    innerRadius: 78,
    outerRadius: 118,
  };
};

const renderActiveShape = ({
  cx,
  cy,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  fill,
  payload,
}) => {
  // For "Others" slice, render with transparent fill and border
  if (payload.isEmpty && payload.label !== '--') {
    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius - 4}
          outerRadius={outerRadius + 8}
          startAngle={startAngle}
          endAngle={endAngle}
          fill="transparent"
          stroke={PROFILE_CATEGORY_EMPTY_COLOR}
          strokeWidth={3}
        />
      </g>
    );
  }

  // Regular active slice
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius - 4}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    </g>
  );
};

const ProfileCategoriesDonutChart = ({ donutData, hasProfilesData }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(getViewportWidth);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(getViewportWidth());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const donutLayout = useMemo(
    () => getDonutLayout(viewportWidth),
    [viewportWidth],
  );

  const colors = hasProfilesData
    ? PROFILE_CATEGORY_COLORS
    : [PROFILE_CATEGORY_DISABLED_COLOR];

  const displayData = useMemo(() => {
    return donutData.map((item, index) => ({
      ...item,
      fill:
        item.isEmpty && item.label !== '--'
          ? 'transparent'
          : hasProfilesData
          ? colors[index % colors.length]
          : PROFILE_CATEGORY_DISABLED_COLOR,
      accentColor:
        item.isEmpty && item.label !== '--'
          ? PROFILE_CATEGORY_OTHER_LABEL_COLOR
          : hasProfilesData
          ? colors[index % colors.length]
          : PROFILE_CATEGORY_DISABLED_COLOR,
    }));
  }, [donutData, hasProfilesData, colors]);

  const activeSlice = activeIndex !== null ? displayData[activeIndex] : null;

  const centerContent = useMemo(() => {
    if (!hasProfilesData) {
      return {
        label: I18n.t('dashboard.profileCategories'),
        value: null,
        count: null,
      };
    }

    if (activeSlice) {
      return {
        label: activeSlice.label,
        value: activeSlice.value,
        count: activeSlice.total,
      };
    }

    return {
      label: I18n.t('dashboard.profileCategories'),
      value: null,
      count: null,
    };
  }, [activeSlice, hasProfilesData]);

  const [labelLineOne, labelLineTwo] = useMemo(
    () => splitCenterLabel(centerContent.label),
    [centerContent.label],
  );

  const isDisabled = !hasProfilesData;

  return (
    <div className={`profile-categories-donut-chart ${isDisabled ? 'disabled' : ''}`}>
      <ResponsiveContainer
        width="100%"
        height={donutLayout.chartHeight}
        minHeight={1}
      >
        <PieChart>
          <Pie
            data={displayData}
            cx="50%"
            cy="50%"
            innerRadius={donutLayout.innerRadius}
            outerRadius={donutLayout.outerRadius}
            paddingAngle={2}
            dataKey="value"
            strokeWidth={0}
            startAngle={180}
            endAngle={-180}
            activeIndex={!isDisabled && activeIndex !== null ? activeIndex : undefined}
            activeShape={renderActiveShape}
            onMouseEnter={(_, index) => !isDisabled && setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(null)}
            isAnimationActive={false}
          >
            {displayData.map((entry, index) => (
              <Cell
                key={`profile-category-cell-${index}`}
                fill={entry.fill}
                stroke={
                  entry.isEmpty && entry.label !== '--'
                    ? PROFILE_CATEGORY_OTHER_LABEL_COLOR
                    : 'transparent'
                }
                strokeWidth={entry.isEmpty && entry.label !== '--' ? 2 : 0}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      <div className="profile-categories-donut-chart__center">
        {centerContent.value !== null && (
          <span
            className="profile-categories-donut-chart__value"
            style={
              activeSlice
                ? {
                    color:
                      activeSlice.isEmpty && activeSlice.label !== '--'
                        ? PROFILE_CATEGORY_OTHER_LABEL_COLOR
                        : activeSlice.accentColor,
                  }
                : undefined
            }
          >
            {`${centerContent.value.toFixed(2)}%`}
          </span>
        )}
        <span
          className={`profile-categories-donut-chart__label ${
            labelLineTwo ? 'profile-categories-donut-chart__label--two-lines' : ''
          }`}
          style={
            activeSlice
              ? {
                  color:
                    activeSlice.isEmpty && activeSlice.label !== '--'
                      ? PROFILE_CATEGORY_OTHER_LABEL_COLOR
                      : activeSlice.accentColor,
                }
              : undefined
          }
        >
          {labelLineTwo ? (
            <>
              <span>{labelLineOne}</span>
              <span>{labelLineTwo}</span>
            </>
          ) : (
            centerContent.label
          )}
        </span>
        {centerContent.count !== null && (
          <span className="profile-categories-donut-chart__count">
            {centerContent.count === 1
              ? `${centerContent.count} ${I18n.t('jobs.profile').toLowerCase()}`
              : `${centerContent.count} ${I18n.t('dashboard.profiles')}`}
          </span>
        )}
      </div>
    </div>
  );
};

export default ProfileCategoriesDonutChart;
